import {
	eventManager as DEBUGGING_EVENT_MANAGER,
	EVENT_TOPICS,
} from '@dealstack/frontend/shared/mechanism/debugging/eventManager';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import i18n from 'i18next';
import { ReactNode } from 'react';
import {
	TOAST_STATE,
	type ShowToastOptions,
} from '@dealstack/frontend/shared/mechanism/toast/useToastV2.types';
import { getUserFacingErrorMessage } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';

const TOAST_ELEMENTS_BY_STATE: {
	[key: string]: {
		icon: JSX.Element;
		bgColor: string;
	};
} = {
	[TOAST_STATE.SUCCESS]: {
		icon: (
			<IconCircleCheck
				size={32}
				stroke={1}
				style={{
					backgroundColor: defaultTheme.v3.colors.teal[2],
					color: defaultTheme.v3.colors.teal[8],
					padding: 3,
					borderRadius: '50%',
				}}
			/>
		),
		bgColor: '#E6FBF5',
	},
	[TOAST_STATE.ERROR]: {
		icon: (
			<div
				style={{
					display: 'inline-flex',
					justifyContent: 'center',
					alignItems: 'center',
					backgroundColor: defaultTheme.v3.colors.red[0],
					borderRadius: '50%',
				}}
			>
				<IconAlertTriangle
					size={32}
					stroke={1}
					style={{
						color: defaultTheme.v3.colors.red[6],
						transform: 'scale(0.7)',
						transformOrigin: 'center',
					}}
				/>
			</div>
		),
		bgColor: '#FFF4F5',
	},
	[TOAST_STATE.WARNING]: {
		icon: (
			<IconAlertCircle
				size={32}
				stroke={1}
				style={{
					backgroundColor: defaultTheme.v3.colors.orange[0],
					color: defaultTheme.v3.colors.orange[6],
					padding: 3,
					borderRadius: '50%',
				}}
			/>
		),
		bgColor: '#E6FBF5',
	},
};
const TOAST_BG_COLOR_BY_THEME = {
	light: {
		bgColor: defaultTheme.v3.colors.white[0],
		fontColor: defaultTheme.v3.colors.dark[8],
		subtitleFontColor: defaultTheme.v3.colors.dark[0],
		borderColor: defaultTheme.v3.colors.gray[2],
	},
};

DEBUGGING_EVENT_MANAGER.register(EVENT_TOPICS.NOTIFICATION_DISMISS_ALL, () => {
	notifications.cleanQueue();
	notifications.clean();
});

export const showToastV2 = (props?: ShowToastOptions) => {
	const defaultTitleByState = {
		[TOAST_STATE.SUCCESS]: i18n.t('translation.toast.default.title.success'),
		[TOAST_STATE.ERROR]: i18n.t('translation.toast.default.title.error'),
	};

	const {
		id,
		title,
		message,
		className,
		dataTestId,
		theme = 'light',
		state = TOAST_STATE.SUCCESS,
		redirecting = false,
	} = props ?? {};
	const defaultTitle = defaultTitleByState[state];

	let toastTitle: string | ReactNode = defaultTitle;
	if (title) {
		toastTitle =
			typeof title === 'string' ? (
				<div>
					<div data-testid={dataTestId ?? TEST_ELEMENT_IDS.DEFAULT_TOAST_TITLE}>
						{title}
					</div>
				</div>
			) : (
				title
			);
	}

	const toastMessage: string | ReactNode =
		typeof message === 'string' ? (
			<div data-testid={dataTestId ?? TEST_ELEMENT_IDS.DEFAULT_TOAST_MESSAGE}>{message}</div>
		) : (
			message
		);

	return notifications.show({
		id,
		title: toastTitle,
		message: toastMessage,
		className,
		withCloseButton: true,
		radius: 8,
		autoClose: redirecting ? 8000 : 4000,
		icon: TOAST_ELEMENTS_BY_STATE[state].icon,
		styles: (_theme) => ({
			root: {
				backgroundColor: TOAST_BG_COLOR_BY_THEME[theme].bgColor,
				boxShadow: '0px 12px 16px -4px #10182814',
				padding: 16,
				borderWidth: 1,
				borderRadius: 8,
				lineHeight: 20,
				borderColor: TOAST_BG_COLOR_BY_THEME[theme].borderColor,
				fontSize: 14,
				'.mantine-Notification-closeButton': {
					marginTop: -50,
					color: defaultTheme.v3.colors.black[0],
				},
				'.mantine-Notification-title': {
					color: TOAST_BG_COLOR_BY_THEME[theme].fontColor,
					fontWeight: 600,
				},
				'.mantine-Notification-description': {
					color: TOAST_BG_COLOR_BY_THEME[theme].subtitleFontColor,
				},
				'.mantine-Notification-icon': {
					width: 40,
					height: 40,
					marginTop: !message ? 0 : -10,
					backgroundColor: TOAST_ELEMENTS_BY_STATE[state].bgColor,
				},
			},
		}),
	});
};

export const toastError = (title: string, error: unknown) =>
	showToastV2({
		title,
		message: getUserFacingErrorMessage(error) ?? 'An unknown error occurred',
		state: 'error',
	});
